import {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

import { DisplayStatus } from 'api/types'
import { STATUS_LABELS } from 'lib/format'
import { t } from 'lib/i18n'

import { Icon, IconName } from './Icon'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  icon?: IconName
  busy?: boolean
  size?: 'md' | 'sm'
}

export const Button = ({
  variant = 'secondary',
  icon,
  busy,
  size = 'md',
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) => (
  <button
    type={type}
    className={['btn', `btn--${variant}`, size === 'sm' ? 'btn--sm' : '', className].filter(Boolean).join(' ')}
    disabled={disabled || busy}
    aria-busy={busy || undefined}
    {...rest}
  >
    {busy ? <span className="spinner spinner--inline" aria-hidden="true" /> : icon ? <Icon name={icon} /> : null}
    {children}
  </button>
)

export const IconButton = ({
  icon,
  label,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string }) => (
  <button
    type="button"
    className={className ? `icon-button ${className}` : 'icon-button'}
    aria-label={label}
    title={label}
    {...rest}
  >
    <Icon name={icon} />
  </button>
)

interface FieldShellProps {
  label: string
  hint?: string
  error?: string
  required?: boolean
  children: (id: string, describedBy?: string) => ReactNode
  className?: string
}

const FieldShell = ({ label, hint, error, required, children, className }: FieldShellProps) => {
  const id = useId()
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined
  return (
    <div className={className ? `field ${className}` : 'field'}>
      <label htmlFor={id} className="field__label">
        {label}
        {required ? <span className="field__required"> *</span> : null}
      </label>
      {children(id, describedBy)}
      {error ? (
        <p id={`${id}-error`} className="field__error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="field__hint">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

type Common = { label: string; hint?: string; error?: string; className?: string }

export const TextField = ({ label, hint, error, className, required, ...rest }: Common & InputHTMLAttributes<HTMLInputElement>) => (
  <FieldShell label={label} hint={hint} error={error} required={required} className={className}>
    {(id, describedBy) => (
      <input
        id={id}
        className="input"
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={describedBy}
        required={required}
        {...rest}
      />
    )}
  </FieldShell>
)

export const PasswordField = ({
  label,
  hint,
  error,
  className,
  required,
  ...rest
}: Common & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) => {
  const [visible, setVisible] = useState(false)
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} className={className}>
      {(id, describedBy) => (
        <div className="field__control">
          <input
            id={id}
            type={visible ? 'text' : 'password'}
            className="input"
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedBy}
            required={required}
            {...rest}
          />
          <button
            type="button"
            className="field__adornment"
            onClick={() => setVisible((value) => !value)}
            aria-label={visible ? t('إخفاء كلمة المرور', 'Hide password') : t('إظهار كلمة المرور', 'Show password')}
          >
            <Icon name={visible ? 'eyeOff' : 'eye'} size={16} />
          </button>
        </div>
      )}
    </FieldShell>
  )
}

export const TextAreaField = ({
  label,
  hint,
  error,
  className,
  required,
  ...rest
}: Common & TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <FieldShell label={label} hint={hint} error={error} required={required} className={className}>
    {(id, describedBy) => (
      <textarea
        id={id}
        className="input input--area"
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={describedBy}
        required={required}
        {...rest}
      />
    )}
  </FieldShell>
)

export const SelectField = ({
  label,
  hint,
  error,
  className,
  required,
  children,
  ...rest
}: Common & SelectHTMLAttributes<HTMLSelectElement>) => (
  <FieldShell label={label} hint={hint} error={error} required={required} className={className}>
    {(id, describedBy) => (
      <select id={id} className="input" aria-describedby={describedBy} required={required} {...rest}>
        {children}
      </select>
    )}
  </FieldShell>
)

export const StatusBadge = ({ status }: { status: DisplayStatus }) => (
  <span className={`badge badge--${status.toLowerCase()}`}>{STATUS_LABELS[status]}</span>
)

export const Skeleton = ({
  className,
  width,
  height,
  circle,
}: {
  className?: string
  width?: string | number
  height?: string | number
  circle?: boolean
}) => (
  <span
    className={['skeleton', circle ? 'skeleton--circle' : '', className].filter(Boolean).join(' ')}
    style={{ width, height }}
    aria-hidden="true"
  />
)

export const Spinner = ({ label }: { label?: string }) => (
  <div className="loading" role="status">
    <span className="spinner" aria-hidden="true" />
    <span>{label ?? t('جارٍ التحميل…', 'Loading…')}</span>
  </div>
)

export const EmptyState = ({ icon = 'folder', title, children }: { icon?: IconName; title: string; children?: ReactNode }) => (
  <div className="empty">
    <Icon name={icon} size={32} />
    <p className="empty__title">{title}</p>
    {children}
  </div>
)

export const ErrorState = ({ message, onRetry }: { message: string; onRetry?: () => void }) => (
  <div className="empty empty--error" role="alert">
    <Icon name="alert" size={32} />
    <p className="empty__title">{message}</p>
    {onRetry ? (
      <Button icon="refresh" onClick={onRetry}>
        {t('إعادة المحاولة', 'Try again')}
      </Button>
    ) : null}
  </div>
)

export const Card = ({
  title,
  actions,
  children,
  className,
  id,
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  id?: string
}) => (
  <section className={className ? `card ${className}` : 'card'} id={id} aria-labelledby={id && title ? `${id}-title` : undefined}>
    {title || actions ? (
      <header className="card__header">
        {title ? (
          <h2 className="card__title" id={id ? `${id}-title` : undefined}>
            {title}
          </h2>
        ) : (
          <span />
        )}
        {actions ? <div className="card__actions">{actions}</div> : null}
      </header>
    ) : null}
    {children}
  </section>
)

export const CollapsibleCard = ({
  title,
  icon,
  id,
  defaultOpen = true,
  headerEnd,
  className,
  children,
}: {
  title: ReactNode
  icon?: IconName
  id?: string
  defaultOpen?: boolean
  headerEnd?: ReactNode
  className?: string
  children: ReactNode
}) => {
  const [open, setOpen] = useState(defaultOpen)
  const bodyId = useId()
  return (
    <section className={className ? `card collapsible-card ${className}` : 'card collapsible-card'} id={id}>
      <div className="collapsible-card__header">
        <button
          type="button"
          className="collapsible-card__toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((value) => !value)}
        >
          <Icon name="chevronDown" size={16} className={open ? 'collapsible-card__chevron' : 'collapsible-card__chevron is-collapsed'} />
          {icon ? (
            <span className="collapsible-card__icon">
              <Icon name={icon} size={16} />
            </span>
          ) : null}
          <span className="collapsible-card__title">{title}</span>
        </button>
        {headerEnd ? <div className="collapsible-card__end">{headerEnd}</div> : null}
      </div>
      {open ? (
        <div className="collapsible-card__body" id={bodyId}>
          {children}
        </div>
      ) : null}
    </section>
  )
}

export const Modal = ({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const focusable = ref.current?.querySelector<HTMLElement>('input, textarea, select, button:not(.modal__close)')
    ;(focusable ?? ref.current)?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus()
    }
  }, [onClose])

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        className={wide ? 'modal modal--wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={ref}
        tabIndex={-1}
      >
        <header className="modal__header">
          <h2 id={titleId}>{title}</h2>
          <IconButton icon="close" label={t('إغلاق', 'Close')} className="modal__close" onClick={onClose} />
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </div>
    </div>
  )
}

/** Slide-over panel anchored to the inline-end edge, for a focused "add one thing" flow (Figma: Sheet). */
export const Sheet = ({
  title,
  onClose,
  children,
  footer,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const focusable = ref.current?.querySelector<HTMLElement>('input, textarea, select, button:not(.modal__close)')
    ;(focusable ?? ref.current)?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus()
    }
  }, [onClose])

  return (
    <div className="side-sheet-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="side-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref} tabIndex={-1}>
        <header className="modal__header">
          <h2 id={titleId}>{title}</h2>
          <IconButton icon="close" label={t('إغلاق', 'Close')} className="modal__close" onClick={onClose} />
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </div>
    </div>
  )
}

export const ProgressBar = ({
  value,
  label,
  tone,
}: {
  value: number
  label: string
  tone?: 'good' | 'warn' | 'danger'
}) => (
  <div
    className={`progress${tone ? ` progress--${tone}` : ''}`}
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={Math.round(value * 100)}
    aria-label={label}
  >
    <span style={{ width: `${Math.round(value * 100)}%` }} />
  </div>
)

export const AvatarStack = ({ names, max = 2 }: { names: string[]; max?: number }) => {
  const shown = names.slice(0, max)
  const extra = names.length - shown.length
  return (
    <div className="avatar-stack">
      {shown.map((name, index) => (
        <span key={`${name}-${index}`} className="shell__avatar avatar-stack__item" title={name}>
          {name.trim().slice(0, 1)}
        </span>
      ))}
      {extra > 0 ? <span className="avatar-stack__more">+{extra}</span> : null}
    </div>
  )
}

export const ProgressRing = ({ value, size = 64, strokeWidth = 7 }: { value: number; size?: number; strokeWidth?: number }) => {
  const pct = Math.max(0, Math.min(1, value))
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="ring" role="img" aria-label={`${Math.round(pct * 100)}%`}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--line-soft)" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--brand)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - pct)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="ring__label">
        {Math.round(pct * 100)}%
      </text>
    </svg>
  )
}

export const PlanBar = ({ actual, planned, label }: { actual: number; planned: number; label: string }) => (
  <div className="plan-bar" role="img" aria-label={label}>
    <span className="plan-bar__fill" style={{ width: `${Math.round(Math.max(0, Math.min(1, actual)) * 100)}%` }} />
    <span className="plan-bar__tick" style={{ insetInlineStart: `${Math.round(Math.max(0, Math.min(1, planned)) * 100)}%` }} />
  </div>
)
